import { ArrowLeft, BookOpen, Layers, Hash, BookMarked, HelpCircle } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Button } from "../components/ui/button";

import CustomTooltip from "../components/CustomTooltip";

export default function GuideCommentairesPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-paper py-8 px-4 sm:px-6 animate-fade-in">
      <div className="max-w-3xl mx-auto space-y-6">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-ink-secondary hover:text-rubric-red hover:bg-paper-dark"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>

        <header className="border-b border-border pb-6 text-center">
          <div className="inline-flex items-center justify-center p-3 bg-amber-100 dark:bg-amber-900/30 rounded-full text-rubric-red mb-3">
            <BookOpen className="h-8 w-8" strokeWidth={1.5} />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl text-ink-primary font-bold">
            Comprendre les Commentaires Fillion
          </h1>
          <p className="font-body text-base text-ink-secondary italic mt-2 max-w-xl mx-auto">
            Mode d'emploi officiel de la numérotation, des divisions hiérarchiques et des abréviations de l'abbé Louis-Claude Fillion.
          </p>
        </header>

        {/* 1. Origine du système */}
        <section className="bg-paper-dark/60 p-5 rounded-lg border border-border space-y-3">
          <h2 className="font-heading text-xl text-ink-primary flex items-center gap-2 font-semibold">
            <Layers className="h-5 w-5 text-rubric-red" />
            1. Pourquoi tant de divisions et de chiffres ?
          </h2>
          <p className="font-body text-sm sm:text-base text-ink-primary leading-relaxed">
            Dans son <em>Avant-propos</em> officiel de 1887, l'abbé Fillion explique qu'il a conçu ses commentaires comme un 
            <strong> plan d'étude méthodique</strong> pour guider le lecteur à travers les saintes Écritures :
          </p>
          <blockquote className="border-l-4 border-rubric-red pl-4 py-2 italic text-sm text-ink-secondary bg-paper rounded-r">
            « Nous avons été préoccupé sans cesse de faciliter la marche du lecteur par des divisions et subdivisions multiples (...). 
            Livres ou parties, sections, paragraphes ; dans les paragraphes, des alinéas numérotés 1°, 2°, etc. ; dans ces alinéas, 
            d'autres séries plus petites de versets : le tout muni de titres, et, pour ainsi dire, dûment étiqueté. Ces groupements équivalent 
            souvent à un commentaire (...). Saint François de Sales aurait dit que ce sont des allées et des sentiers, qui permettent de se promener à l'aise dans le beau jardin des Écritures. »
          </blockquote>
        </section>

        {/* 2. Exemple décrypté ligne par ligne */}
        <section className="bg-paper-dark/60 p-5 rounded-lg border border-border space-y-4">
          <h2 className="font-heading text-xl text-ink-primary flex items-center gap-2 font-semibold">
            <Hash className="h-5 w-5 text-rubric-red" />
            2. Décryptage d'un exemple concret (Lévitique 1:2)
          </h2>
          <p className="font-body text-sm text-ink-secondary">
            Voici la décomposition pas à pas d'un commentaire typique de Fillion :
          </p>

          <div className="p-3 sm:p-4 bg-amber-50 dark:bg-card/40 rounded border border-amber-200 dark:border-border font-mono text-xs sm:text-sm text-ink-primary space-y-1">
            <p className="font-semibold text-rubric-red font-body">Exemple dans le texte :</p>
            <p>« I. — Les holocaustes. I, 1 — 17. 1° Holocaustes de gros bétail, vers. 1 — 9. Chap. I. — 1 — 2a. »</p>
          </div>

          <div className="grid gap-3 pt-2">
            <div className="p-3 bg-paper rounded border border-border flex items-start gap-3">
              <span className="font-mono font-bold text-rubric-red text-sm px-2 py-0.5 bg-amber-100 rounded">I.</span>
              <div className="text-sm">
                <p className="font-semibold text-ink-primary">Grande Section du Livre</p>
                <p className="text-ink-secondary">Chiffre romain majuscule désignant la partie thématique du livre biblique (ex. Section I : Les holocaustes).</p>
              </div>
            </div>

            <div className="p-3 bg-paper rounded border border-border flex items-start gap-3">
              <span className="font-mono font-bold text-rubric-red text-sm px-2 py-0.5 bg-amber-100 rounded">I, 1 — 17</span>
              <div className="text-sm">
                <p className="font-semibold text-ink-primary">Chapitre et plage de versets</p>
                <p className="text-ink-secondary">Le chiffre romain <strong>I</strong> = le chapitre. Le tiret <strong>1 — 17</strong> = versets 1 à 17 couverts par cette section.</p>
              </div>
            </div>

            <div className="p-3 bg-paper rounded border border-border flex items-start gap-3">
              <span className="font-mono font-bold text-rubric-red text-sm px-2 py-0.5 bg-amber-100 rounded">1°</span>
              <div className="text-sm">
                <p className="font-semibold text-ink-primary">Alinéa / Paragraphe de premier niveau</p>
                <p className="text-ink-secondary">Nombre ordinal (1°, 2°, 3°, etc.) subdivisant la grande section en sous-thèmes d'exégèse.</p>
              </div>
            </div>

            <div className="p-3 bg-paper rounded border border-border flex items-start gap-3">
              <span className="font-mono font-bold text-rubric-red text-sm px-2 py-0.5 bg-amber-100 rounded">1 — 2a</span>
              <div className="text-sm">
                <p className="font-semibold text-ink-primary">Sous-découpage de verset (Hémistiche)</p>
                <p className="text-ink-secondary">La lettre <strong>a</strong> désigne la première moitié du verset, la lettre <strong>b</strong> la seconde moitié (ex. verset 1 jusqu'au milieu du verset 2).</p>
              </div>
            </div>

            <div className="p-3 bg-paper rounded border border-border flex items-start gap-3">
              <span className="font-mono font-bold text-rubric-red text-sm px-2 py-0.5 bg-amber-100 rounded">Vocavit...</span>
              <div className="text-sm">
                <p className="font-semibold text-ink-primary">Lemme latin de la Vulgate</p>
                <p className="text-ink-secondary">Les premiers mots du texte latin de saint Jérôme, indiquant exactement quelle phrase biblique est commentée.</p>
              </div>
            </div>
          </div>
        </section>

        {/* 3. Table des Références et Abréviations */}
        <section className="bg-paper-dark/60 p-5 rounded-lg border border-border space-y-4">
          <h2 className="font-heading text-xl text-ink-primary flex items-center gap-2 font-semibold">
            <BookMarked className="h-5 w-5 text-rubric-red" />
            3. Guide des abréviations courantes de Fillion
          </h2>
          <div className="bg-amber-100/50 p-3 rounded text-sm text-ink-secondary mb-2">
            <strong className="text-rubric-red">💡 Astuce :</strong> Dans le texte des commentaires, vous pouvez survoler les abréviations pointillées pour voir leur signification (ex: <CustomTooltip triggerText="Man. bibl." fullText="Manuel Biblique (ou Cours d'Écriture Sainte)" />). 
            Vous pouvez également consulter le <Link to="/glossaire" className="text-rubric-red hover:underline font-semibold">Glossaire complet</Link>.
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left border-collapse">
              <thead>
                <tr className="border-b border-border text-ink-primary bg-amber-100/50">
                  <th className="py-2.5 px-3 font-semibold">Abréviation</th>
                  <th className="py-2.5 px-3 font-semibold">Signification</th>
                  <th className="py-2.5 px-3 font-semibold">Exemple</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border font-body text-ink-secondary">
                <tr>
                  <td className="py-2 px-3 font-mono font-medium text-rubric-red">Comp. / Cf.</td>
                  <td className="py-2 px-3">Comparez / Voyez à tel passage</td>
                  <td className="py-2 px-3 italic">Comp. Gen. xxv, 15</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono font-medium text-rubric-red">et ss. / et s.</td>
                  <td className="py-2 px-3">Et suivants (latin <em>sequentesque</em>)</td>
                  <td className="py-2 px-3 italic">Ex. xix, 3 et ss.</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono font-medium text-rubric-red">Vulg.</td>
                  <td className="py-2 px-3">Texte latin officiel de la Vulgate clémentine</td>
                  <td className="py-2 px-3 italic">Vulg. : delicta</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono font-medium text-rubric-red">Sept. / LXX</td>
                  <td className="py-2 px-3">Version grecque de la Septante (IIIe s. av. J.-C.)</td>
                  <td className="py-2 px-3 italic">d'après les LXX</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono font-medium text-rubric-red">hébr. / gr. / lat.</td>
                  <td className="py-2 px-3">Texte hébreu / grec / latin original</td>
                  <td className="py-2 px-3 italic">l'hébr. qorbân</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono font-medium text-rubric-red">c.-à-d.</td>
                  <td className="py-2 px-3">C'est-à-dire</td>
                  <td className="py-2 px-3 italic">c.-à-d. le peuple entier</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono font-medium text-rubric-red">Atl. archéol. / géogr.</td>
                  <td className="py-2 px-3">Atlas archéologique ou géographique de la Bible Fillion</td>
                  <td className="py-2 px-3 italic">Atl. arch., pl. CIV, fig. 4</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono font-medium text-rubric-red">pl. / fig. / col.</td>
                  <td className="py-2 px-3">Planche / Figure / Colonne de l'Atlas</td>
                  <td className="py-2 px-3 italic">pl. VII, fig. 12</td>
                </tr>
                <tr>
                  <td className="py-2 px-3 font-mono font-medium text-rubric-red">Reg.</td>
                  <td className="py-2 px-3">Livres des Rois (I et II Samuel = I et II Rois dans la Vulgate)</td>
                  <td className="py-2 px-3 italic">I Reg. XI, 8</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* 4. Où sont les illustrations ? (Atlas Archéologique) */}
        <section className="bg-paper-dark/60 p-5 rounded-lg border border-border space-y-3">
          <h2 className="font-heading text-xl text-ink-primary flex items-center gap-2 font-semibold">
            <BookOpen className="h-5 w-5 text-rubric-red" />
            4. Où sont les illustrations de l'abbé Fillion ?
          </h2>
          <p className="font-body text-sm sm:text-base text-ink-primary leading-relaxed">
            Lorsque vous lisez dans les commentaires des mentions récurrentes telles que :
          </p>
          <div className="p-3 bg-paper rounded border border-border font-serif italic text-sm text-ink-secondary">
            « Voy. l'Atl. archéol., pl. XCVI, fig. 1 » ou « Voyez l'Atl. géogr., pl. XIV »
          </div>
          <p className="font-body text-sm sm:text-base text-ink-primary leading-relaxed">
            <strong>Pourquoi ces gravures ne sont-elles pas dans le texte ?</strong>
          </p>
          <ul className="list-disc list-inside text-sm sm:text-base text-ink-secondary space-y-2 leading-relaxed">
            <li>
              Dans l'édition originale du XIX<sup>e</sup> siècle, l'abbé Fillion et la maison Letouzey ont fait le choix éditorial de séparer les <strong>8 tomes de commentaires théologiques</strong> d'un volume volumineux indépendant : l'<strong>Atlas Archéologique et Géographique de la Bible</strong>.
            </li>
            <li>
              Ce volume d'Atlas contient toutes les planches grand format, gravures sur cuivre, cartes géographiques et relevés topographiques (le Tabernacle, l'Arche d'Alliance, le Temple de Salomon, les monnaies antiques, les fouilles d'Égypte et de Chaldée).
            </li>
            <li>
              Les 8 volumes de commentaires de la Bible textuelle ne contenaient donc pas de gravures imbriquées dans le corps de texte, mais renvoyaient le lecteur à cet Atlas séparé.
            </li>
          </ul>
        </section>

        {/* 5. Astuce de lecture */}
        <section className="bg-amber-50 dark:bg-card/40 p-5 rounded-lg border border-amber-300 dark:border-border space-y-2">
          <h2 className="font-heading text-lg text-amber-900 dark:text-amber-300 font-semibold flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-rubric-red" />
            Astuce de lecture pour le fidèle d'aujourd'hui
          </h2>
          <p className="font-body text-sm text-ink-primary leading-relaxed">
            Si vous lisez les commentaires pour votre édification spirituelle ou votre prière quotidienne, 
            <strong> vous pouvez glisser sur les chiffres de structure</strong> (les sections <em>I.</em>, <em>1°</em>, les renvois d'atlas) 
            et vous concentrer directement sur l'explication théologique, spirituelle et historique qui suit chaque tiret (—).
          </p>
        </section>

        <div className="pt-4 flex justify-center">
          <Button asChild className="bg-rubric-red hover:bg-rubric-red/90 text-white font-body">
            <Link to="/bible/genese/1">Commencer la lecture de la Bible</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
